import Tippy from '@tippyjs/react';
import { Stack } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../../components/buttons/SimpleButton';
import { SimpleTable } from '../../../../../../components/tables/SimpleTable';
import { Course } from '../../../../../../types/course.type';
import {
  enrollmentColumns,
  flatRateenrollmentColumns,
  getRosterColumns,
  waitlistColumns,
  waiversColumns
} from '../../../../../../utils/consts/columnDefs/sessionColumns';
import { getCurrentUser } from '../../../../../../services/helper';

interface Props {
  course: Course | undefined;
  featuredTlps: any[];
  onWelcomeEmailClick: (email: string) => void;
}

export const SessionRosterOrEnrollment = ({ course, featuredTlps, onWelcomeEmailClick }: Props) => {
  const currentUser = getCurrentUser();
  const navigate = useNavigate();

  const totalStudents = course?.enrollments?.reduce((acc, e) => acc + (e.quantity ?? 0), 0) ?? 0;

  const formattedTotal = parseFloat(
    (course?.sum_enrollment_values ?? '0.0') as string
  ).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const avgTuition = (() => {
    const revenue = Number(course?.sum_enrollment_values ?? 0);
    if (totalStudents <= 0) return '0.00';
    return parseFloat((revenue / totalStudents).toFixed(2)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  })();

  if (course?.payment_type === 'website') {
    return (
      <Stack gap={3}>
          {!currentUser?.isInstructor && (
              <Link
                  to={{ pathname: '../../../../admin/registrations', search: `?course_id=${course?.id}` }}
                  className="text-info"
              >
                  Manage Registrations
              </Link>
          )}
        <SimpleTable
          columns={getRosterColumns(onWelcomeEmailClick)}
          data={
            course?.registrations?.map((reg) => {
              const child = reg?.child;
              const social = (child?.social_emotional_needs ?? '').trim();
              const legacy = (child?.emotional_needs ?? '').trim();
              return {
                ...child,
                social_emotional_needs: social || legacy,
                course,
                featured_tlps: featuredTlps
              };
            }) ?? []
          }
        />
      </Stack>
    );
  }

  return (
    <div>
      <Stack gap={3}>
        <SimpleTable
          columns={
            course?.invoice_type === 'flat_rate' ? flatRateenrollmentColumns : enrollmentColumns
          }
          data={
            course?.enrollments?.map((enrollment) => ({
              ...enrollment,
              course,
              featured_tlps: featuredTlps
            })) ?? []
          }
          footer={(column) => {
            if (column.id === 'total') return <span>${formattedTotal}</span>;
            if (column.id === 'number_of_students_expected') return <span>{totalStudents}</span>;
            if (column.id === 'actions')
              return (
                <span>
                  <Tippy content={`$${avgTuition}`}>
                    <i className="fa fa-users" />
                  </Tippy>
                </span>
              );
            return <></>;
          }}
        />
      </Stack>
        <div className="w-100 d-flex justify-content-end my-3">
        <SimpleButton
          variant="sfc-blueberry"
          height={40}
          width={320}
          onClick={() => navigate('/admin/enrollments/new/' + course?.id)}
        >
          New enrollment
        </SimpleButton>
      </div>
    </div>
  );
};

export const SessionWaitlistOrWaivers = ({
  course,
  featuredTlps
}: Omit<Props, 'onWelcomeEmailClick'>) => {
  if (course?.payment_type === 'website') {
    return (
      <SimpleTable
        columns={waitlistColumns}
        data={
          course?.children?.map((child) => ({
            ...child,
            course,
            featured_tlps: featuredTlps
          })) ?? []
        }
      />
    );
  }

  return (
    <SimpleTable
      columns={waiversColumns}
      data={
        course?.waivers?.map((waiver) => ({
          ...waiver,
          course,
          featured_tlps: featuredTlps
        })) ?? []
      }
    />
  );
};
